import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import Loader from "../../../components/ui/Loader";
import Modal from "../../../components/ui/Modal";
import Textarea from "../../../components/ui/Textarea";
import { useToast } from "../../../contexts/ToastContext";
import {
	AgreementStatus,
	useGetTenancyAgreementQuery,
	useUpdateTenancyAgreementMutation,
} from "../../../store/api/tenancyAgreementsApi";

const TenancyAgreementReview: React.FC = () => {
	const { id } = useParams();
	const navigate = useNavigate();
	const { success, error: showError } = useToast();
	const [isAcceptModalOpen, setIsAcceptModalOpen] = useState(false);
	const [isRequestChangesModalOpen, setIsRequestChangesModalOpen] =
		useState(false);
	const [tenantInitials, setTenantInitials] = useState("");
	const [tenantNotes, setTenantNotes] = useState("");

	const {
		data: agreement,
		isLoading,
		error,
		refetch,
	} = useGetTenancyAgreementQuery(id || "", { skip: !id });

	const [updateAgreement, { isLoading: isUpdating }] =
		useUpdateTenancyAgreementMutation();

	const formatDate = (dateString?: string) => {
		if (!dateString) return "N/A";
		return new Date(dateString).toLocaleDateString("en-US", {
			year: "numeric",
			month: "long",
			day: "numeric",
		});
	};

	const handleAccept = async () => {
		if (!tenantInitials.trim()) {
			showError("Please enter your initials to accept the agreement");
			return;
		}

		try {
			await updateAgreement({
				id: id!,
				data: {
					status: AgreementStatus.ACCEPTED,
					tenant_initials: tenantInitials.trim(),
				},
			}).unwrap();
			success("Agreement accepted successfully!");
			setIsAcceptModalOpen(false);
			setTenantInitials("");
			refetch();
		} catch (err: any) {
			console.error("Failed to accept agreement:", err);
			showError(
				err?.data?.message || "Failed to accept agreement. Please try again.",
			);
		}
	};

	const handleRequestChanges = async () => {
		if (!tenantNotes.trim()) {
			showError("Please provide details about the requested changes");
			return;
		}

		try {
			await updateAgreement({
				id: id!,
				data: {
					status: AgreementStatus.PENDING_CHANGES,
					tenant_notes: tenantNotes.trim(),
				},
			}).unwrap();
			success("Change request submitted successfully!");
			setIsRequestChangesModalOpen(false);
			setTenantNotes("");
			refetch();
		} catch (err: any) {
			console.error("Failed to request changes:", err);
			showError(
				err?.data?.message ||
					"Failed to submit change request. Please try again.",
			);
		}
	};

	if (isLoading) {
		return (
			<Loader size="lg" showText text="Loading agreement..." className="min-h-screen" />
		);
	}

	if (error || !agreement) {
		return (
			<div className="flex flex-col items-center justify-center min-h-screen p-6">
				<Icon name="AlertCircle" size={48} className="text-error mb-4" />
				<h2 className="text-xl font-semibold text-foreground mb-2">
					Agreement Not Found
				</h2>
				<p className="text-muted-foreground mb-4">
					The tenancy agreement you're looking for doesn't exist or you don't
					have access to it.
				</p>
				<Button onClick={() => navigate("/tenant-dashboard")}>
					Go to Dashboard
				</Button>
			</div>
		);
	}

	const canAccept =
		agreement.status === AgreementStatus.DRAFT ||
		agreement.status === AgreementStatus.PENDING_REVIEW;
	const canRequestChanges = canAccept;

	return (
		<div className="min-h-screen bg-background p-6">
			<div className="max-w-4xl mx-auto">
				{/* Header */}
				<div className="mb-6">
					<Button
						variant="ghost"
						onClick={() => navigate("/tenant-dashboard")}
						iconName="ArrowLeft"
						iconPosition="left"
						className="mb-4"
					>
						Back to Dashboard
					</Button>
					<h1 className="text-3xl font-bold text-foreground mb-2">
						Tenancy Agreement Review
					</h1>
					<p className="text-muted-foreground">
						Property: {agreement.property_name || "N/A"}
					</p>
				</div>

				{/* Agreement Info Card */}
				<div className="bg-card rounded-lg border border-border p-6 mb-6">
					<div className="flex items-center justify-between mb-4">
						<div>
							<h2 className="text-xl font-semibold text-foreground mb-2">
								Tenancy Agreement v{agreement.version}
							</h2>
							<div className="flex items-center space-x-4 text-sm text-muted-foreground">
								<span>Uploaded: {formatDate(agreement.uploaded_at)}</span>
								{agreement.uploaded_by_name && (
									<span>by {agreement.uploaded_by_name}</span>
								)}
							</div>
						</div>
						<span
							className={`px-3 py-1 rounded-full text-sm font-medium ${
								agreement.status === AgreementStatus.ACCEPTED
									? "bg-success/10 text-success"
									: agreement.status === AgreementStatus.PENDING_CHANGES
									? "bg-warning/10 text-warning"
									: agreement.status === AgreementStatus.REJECTED
									? "bg-error/10 text-error"
									: "bg-muted/10 text-muted-foreground"
							}`}
						>
							{agreement.status.replace("_", " ").toUpperCase()}
						</span>
					</div>

					{/* Status Messages */}
					{agreement.status === AgreementStatus.ACCEPTED && (
						<div className="bg-success/10 border border-success rounded-lg p-4 mb-4">
							<div className="flex items-center space-x-2 text-success">
								<Icon name="CheckCircle" size={20} />
								<p className="font-medium">
									You have accepted this agreement on{" "}
									{formatDate(agreement.tenant_signed_at)}
								</p>
							</div>
							{agreement.tenant_initials && (
								<p className="text-sm text-success font-medium mt-2">
									Initials: {agreement.tenant_initials}
								</p>
							)}
						</div>
					)}

					{agreement.status === AgreementStatus.PENDING_CHANGES && (
						<div className="bg-warning/10 border border-warning rounded-lg p-4 mb-4">
							<div className="flex items-center space-x-2 text-warning">
								<Icon name="AlertCircle" size={20} />
								<p className="font-medium">
									You have requested changes to this agreement
								</p>
							</div>
							{agreement.tenant_notes && (
								<div className="mt-2">
									<p className="text-sm font-medium text-warning">
										Your Requested Changes:
									</p>
									<p className="text-sm text-foreground mt-1">
										{agreement.tenant_notes}
									</p>
								</div>
							)}
							{agreement.admin_notes && (
								<div className="mt-2">
									<p className="text-sm font-medium text-warning">
										Admin Response:
									</p>
									<p className="text-sm text-foreground mt-1">
										{agreement.admin_notes}
									</p>
								</div>
							)}
						</div>
					)}

					{/* Admin Notes */}
					{agreement.admin_notes &&
						agreement.status !== AgreementStatus.PENDING_CHANGES && (
							<div className="bg-muted/30 rounded-lg p-4 mb-4">
								<p className="text-sm font-medium text-foreground mb-1">
									Admin Notes:
								</p>
								<p className="text-sm text-muted-foreground">
									{agreement.admin_notes}
								</p>
							</div>
						)}

					{/* Document Preview */}
					<div className="mb-6">
						<div className="bg-muted/30 rounded-lg p-4 flex items-center justify-between">
							<div className="flex items-center space-x-3">
								<div className="w-12 h-12 bg-primary/10 text-primary rounded-lg flex items-center justify-center">
									<Icon name="FileText" size={24} />
								</div>
								<div>
									<p className="font-medium text-foreground">
										Tenancy Agreement Document
									</p>
									<p className="text-sm text-muted-foreground">
										Version {agreement.version}
									</p>
								</div>
							</div>
							<Button
								variant="outline"
								onClick={() => window.open(agreement.document_url, "_blank")}
								iconName="ExternalLink"
							>
								View Document
							</Button>
						</div>
					</div>

					{/* Action Buttons */}
					{canAccept && (
						<div className="flex space-x-3">
							<Button
								variant="success"
								onClick={() => setIsAcceptModalOpen(true)}
								iconName="Check"
								className="flex-1"
							>
								Accept Agreement
							</Button>
							<Button
								variant="outline"
								onClick={() => setIsRequestChangesModalOpen(true)}
								iconName="Edit"
								className="flex-1"
							>
								Request Changes
							</Button>
						</div>
					)}

					{agreement.status === AgreementStatus.ACCEPTED && (
						<div className="flex space-x-3">
							<Button
								variant="outline"
								onClick={() => window.open(agreement.document_url, "_blank")}
								iconName="Download"
								className="flex-1"
							>
								Download PDF
							</Button>
						</div>
					)}
				</div>
			</div>

			{/* Accept Modal */}
			<Modal
				isOpen={isAcceptModalOpen}
				onClose={() => {
					setIsAcceptModalOpen(false);
					setTenantInitials("");
				}}
				title="Accept Tenancy Agreement"
				description="Please enter your initials to confirm acceptance of this agreement"
			>
				<div className="space-y-4">
					<p className="text-sm text-muted-foreground">
						By entering your initials below, you confirm that you have read and
						agree to the terms of this tenancy agreement.
					</p>
					<Input
						label="Your Initials"
						value={tenantInitials}
						onChange={(e) => setTenantInitials(e.target.value.toUpperCase())}
						placeholder="e.g., JD"
						maxLength={10}
						required
					/>
					<div className="flex justify-end space-x-3 pt-4 border-t border-border">
						<Button
							variant="outline"
							onClick={() => {
								setIsAcceptModalOpen(false);
								setTenantInitials("");
							}}
						>
							Cancel
						</Button>
						<Button
							variant="success"
							onClick={handleAccept}
							disabled={!tenantInitials.trim() || isUpdating}
						>
							{isUpdating ? "Accepting..." : "Accept Agreement"}
						</Button>
					</div>
				</div>
			</Modal>

			{/* Request Changes Modal */}
			<Modal
				isOpen={isRequestChangesModalOpen}
				onClose={() => {
					setIsRequestChangesModalOpen(false);
					setTenantNotes("");
				}}
				title="Request Changes to Agreement"
				description="Please provide details about the changes you would like to request"
			>
				<div className="space-y-4">
					<p className="text-sm text-muted-foreground">
						Please describe the specific changes you would like to request. The
						admin will review your request and respond accordingly.
					</p>
					<Textarea
						label="Requested Changes"
						value={tenantNotes}
						onChange={(e) => setTenantNotes(e.target.value)}
						placeholder="Please describe the changes you would like..."
						rows={6}
						required
					/>
					<div className="flex justify-end space-x-3 pt-4 border-t border-border">
						<Button
							variant="outline"
							onClick={() => {
								setIsRequestChangesModalOpen(false);
								setTenantNotes("");
							}}
						>
							Cancel
						</Button>
						<Button
							variant="warning"
							onClick={handleRequestChanges}
							disabled={!tenantNotes.trim() || isUpdating}
						>
							{isUpdating ? "Submitting..." : "Submit Request"}
						</Button>
					</div>
				</div>
			</Modal>
		</div>
	);
};

export default TenancyAgreementReview;
